import { HTMLAttributes, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@material-tailwind/react';
import { decode } from 'js-base64';
import Cookies from 'js-cookie';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import * as z from 'zod';

import { api } from '@plugins/axios/api';

import { useAlert } from '@hooks/useAlert';
import { useRecoil } from '@hooks/useRecoil';

import { InputField } from '@components/field/InputField';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@components/form/parts';

import { cn } from '@utils/tailwind/cn';

export interface LoginFormProps extends HTMLAttributes<HTMLDivElement> {}

const formSchema = z.object({
  id: z.string().min(1, '아이디를 입력해주세요'),
  pw: z.string().min(1, '패스워드를 입력해주세요'),
});

export const LoginForm = ({ className, ...props }: LoginFormProps) => {
  const { alertClick } = useAlert();
  const navigate = useNavigate();
  const { useRecoilState, useAtom } = useRecoil();
  const [profile, setProfile] = useRecoilState(
    useAtom<{ uid: number; role: 'partner' | 'funeral'; name: string } | null>(
      'USER_PROFILE',
    ),
  );

  useEffect(() => {
    if (profile !== null) {
      if (profile.role === 'partner') {
        navigate('/partner');
      } else if (profile.role === 'funeral') {
        navigate('/funeral');
      }
    }
  }, [profile, navigate]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      id: '',
      pw: '',
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    api
      .post('/auth/login', {
        id: values['id'],
        pw: values['pw'],
      })
      .then(({ data }) => {
        if ('accessToken' in data) {
          Cookies.set('REPLHADA_ADMIN_ACCESS_TOKEN', data['accessToken']);
        }
        if ('refreshToken' in data) {
          Cookies.set('REPLHADA_ADMIN_REFRESH_TOKEN', data['refreshToken']);
        }
        const { uid, role, name } = JSON.parse(
          decode(data['refreshToken'].split('.')[1]),
        );
        setProfile({
          uid: Number(uid),
          role,
          name,
        });
        // navigate('/');
        resetAndHandle();
      })
      .catch((err) => {
        console.error('Fetch err', err);
        alertClick(
          {
            title: '로그인 실패 ',
            description: 'ID와 PW 를 다시 확인해주세요',
          },
          {
            onClick: () => {
              resetAndHandle();
            },
          },
        );
      });
  };

  const resetAndHandle = () => {
    form.reset();
  };

  return (
    <div className={cn('flex flex-col gap-6', className)} {...props}>
      <Form {...form}>
        <div className='flex w-full flex-col space-y-4'>
          <FormField
            control={form.control}
            name='id'
            render={({ field }) => (
              <FormItem className='flex flex-col flex-wrap md:flex-row'>
                <FormLabel className='flex w-12 items-center justify-start text-sm md:text-base'>
                  ID
                </FormLabel>
                <FormControl className='flex-1'>
                  <InputField
                    className='text-sm'
                    placeholder='아이디'
                    {...field}
                  />
                </FormControl>
                <FormMessage className='w-full py-1 pl-14 text-xs' />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='pw'
            render={({ field }) => (
              <FormItem className='flex flex-col flex-wrap md:flex-row'>
                <FormLabel className='flex w-12 items-center justify-start text-sm md:text-base'>
                  PW
                </FormLabel>
                <FormControl className='flex-1'>
                  <InputField
                    className='text-sm'
                    type='password'
                    placeholder='비밀번호'
                    {...field}
                  />
                </FormControl>
                <FormMessage className='w-full py-1 pl-14 text-xs' />
              </FormItem>
            )}
          />
        </div>
      </Form>
      <div className='flex flex-row justify-center'>
        <Button
          size='lg'
          className='w-full min-w-[140px] rounded-sm py-3'
          onClick={form.handleSubmit(onSubmit)}
        >
          <span>로그인</span>
        </Button>
      </div>
    </div>
  );
};
